import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { Link, useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, ChevronLeft, Truck, Car, Bike, Clock, Banknote, MapPin, Star, Lock, Edit2 } from "lucide-react";

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

const VEHICLES = [
  { value: "bike", label: "Bicycle", icon: Bike, desc: "Small orders, short distances" },
  { value: "car",  label: "Car",     icon: Car,  desc: "Standard deliveries" },
  { value: "van",  label: "Van",     icon: Truck, desc: "Large or multiple orders" },
];

const BENEFITS = [
  { icon: Banknote, title: "Earn per delivery",     desc: "Get paid for every order you complete. Fees set by the platform." },
  { icon: Clock,    title: "Flexible hours",         desc: "Go online when you want. No shifts, no minimum hours." },
  { icon: MapPin,   title: "Stay local",             desc: "Deliver within Ayrshire — never far from home." },
  { icon: Star,     title: "Build your reputation",  desc: "Earn customer ratings and stand out in the driver pool." },
];

const STEPS = [
  { n: "1", title: "Apply",          body: "Fill in your vehicle type, licence number, and a short bio." },
  { n: "2", title: "Get approved",   body: "Our team reviews your application, typically within 24 hours." },
  { n: "3", title: "Start earning",  body: "Go online in the mobile app and start accepting deliveries." },
];

type DriverStatus = "loading" | "none" | "pending" | "approved" | "rejected";

export default function BecomeDriver() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  const [status, setStatus]           = useState<DriverStatus>("loading");
  const [vehicleType, setVehicleType] = useState("car");
  const [licence, setLicence]         = useState("");
  const [bio, setBio]                 = useState("");
  const [vehicleReg, setVehicleReg]               = useState("");
  const [isInsured, setIsInsured]                 = useState(false);
  const [bankAccountName, setBankAccountName]     = useState("");
  const [bankSortCode, setBankSortCode]           = useState("");
  const [bankAccountNumber, setBankAccountNumber] = useState("");
  const [submitting, setSubmitting]               = useState(false);

  // Bank details update (for approved drivers)
  const [currentBankName, setCurrentBankName]     = useState<string | null>(null);
  const [currentBankSort, setCurrentBankSort]     = useState<string | null>(null);
  const [currentBankNumber, setCurrentBankNumber] = useState<string | null>(null);
  const [showBankForm, setShowBankForm]           = useState(false);
  const [updBankName, setUpdBankName]             = useState("");
  const [updBankSort, setUpdBankSort]             = useState("");
  const [updBankNumber, setUpdBankNumber]         = useState("");
  const [savingBank, setSavingBank]               = useState(false);

  useEffect(() => {
    if (!user) { setStatus("none"); return; }
    fetch("/api/drivers/profile", { headers: authHeaders() })
      .then(r => (r.ok ? r.json() : null))
      .then((d: { approvedAt?: string | null; rejectedAt?: string | null; bankAccountName?: string | null; bankSortCode?: string | null; bankAccountNumber?: string | null } | null) => {
        if (!d) { setStatus("none"); return; }
        if (d.approvedAt) {
          setStatus("approved");
          setCurrentBankName(d.bankAccountName ?? null);
          setCurrentBankSort(d.bankSortCode ?? null);
          setCurrentBankNumber(d.bankAccountNumber ?? null);
          return;
        }
        if (d.rejectedAt) { setStatus("rejected"); return; }
        setStatus("pending");
      })
      .catch(() => setStatus("none"));
  }, [user]);

  async function handleUpdateBank() {
    if (!updBankName.trim() || !updBankSort.trim() || !updBankNumber.trim()) {
      toast({ title: "Please fill in all three bank fields", variant: "destructive" }); return;
    }
    setSavingBank(true);
    try {
      const res = await fetch("/api/drivers/profile", {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ bankAccountName: updBankName.trim(), bankSortCode: updBankSort.trim(), bankAccountNumber: updBankNumber.trim() }),
      });
      const json = await res.json() as { error?: string };
      if (!res.ok) throw new Error(json.error ?? "Could not update bank details");
      setCurrentBankName(updBankName.trim());
      setCurrentBankSort(updBankSort.trim());
      setCurrentBankNumber(updBankNumber.trim());
      setUpdBankName(""); setUpdBankSort(""); setUpdBankNumber("");
      setShowBankForm(false);
      toast({ title: "Bank details updated", description: "Your new details will be used for future payouts." });
    } catch (e: unknown) {
      toast({ title: "Error", description: (e as Error).message, variant: "destructive" });
    } finally {
      setSavingBank(false);
    }
  }

  useEffect(() => {
    if (!user) setStatus("none");
  }, [user]);

  const handleSubmit = async () => {
    if (!licence.trim())           { toast({ title: "Please enter your driving licence number", variant: "destructive" }); return; }
    if (!vehicleReg.trim())        { toast({ title: "Please enter your vehicle registration", variant: "destructive" }); return; }
    if (!isInsured)                { toast({ title: "Please confirm you hold valid vehicle insurance to proceed", variant: "destructive" }); return; }
    if (!bankAccountName.trim())   { toast({ title: "Please enter the name on your bank account", variant: "destructive" }); return; }
    if (!bankSortCode.trim())      { toast({ title: "Please enter your bank sort code", variant: "destructive" }); return; }
    if (!bankAccountNumber.trim()) { toast({ title: "Please enter your bank account number", variant: "destructive" }); return; }
    if (!bio.trim())               { toast({ title: "Please write a short bio", variant: "destructive" }); return; }
    setSubmitting(true);
    try {
      const res = await fetch("/api/drivers/apply", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ vehicleType, licenceNumber: licence.trim(), bio: bio.trim(), vehicleReg: vehicleReg.trim(), isInsured, bankAccountName: bankAccountName.trim(), bankSortCode: bankSortCode.trim(), bankAccountNumber: bankAccountNumber.trim() }),
      });
      const json = await res.json() as { error?: string };
      if (!res.ok) throw new Error(json.error ?? "Could not submit application");
      setStatus("pending");
      toast({ title: "Application submitted!", description: "We'll review it and notify you soon." });
    } catch (e: unknown) {
      toast({ title: "Error", description: (e as Error).message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero */}
      <div className="bg-primary px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <button onClick={() => window.history.back()} className="flex items-center gap-1 text-sm text-white/70 hover:text-white mb-5 transition-colors">
            <ChevronLeft size={17} /> Back
          </button>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-accent flex items-center justify-center flex-shrink-0">
              <Truck size={26} className="text-white" />
            </div>
            <div>
              <h1 className="text-white font-bold text-2xl leading-tight">Become a Driver and Earn Today!</h1>
              <p className="text-white/75 text-sm mt-1">Deliver for local Ayrshire businesses on your own schedule.</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-6">
            {[
              { value: "Flexible", label: "hours" },
              { value: "Local",    label: "deliveries only" },
              { value: "Fast",     label: "application review" },
            ].map(({ value, label }) => (
              <div key={label} className="bg-white/10 rounded-xl px-3 py-2.5 text-center">
                <p className="text-white font-bold text-sm">{value}</p>
                <p className="text-white/65 text-xs mt-0.5">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-7 space-y-6">

        {/* Benefits */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-4">
          <h2 className="font-bold text-slate-800">Why drive with us?</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {BENEFITS.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-primary/8 flex items-center justify-center flex-shrink-0">
                  <Icon size={17} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">{title}</p>
                  <p className="text-xs text-slate-500 leading-relaxed mt-0.5">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* How it works */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-4">
          <h2 className="font-bold text-slate-800">How it works</h2>
          <div className="space-y-3">
            {STEPS.map(({ n, title, body }) => (
              <div key={n} className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-xs font-bold">{n}</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">{title}</p>
                  <p className="text-xs text-slate-500 leading-relaxed">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Status / Form area */}
        {status === "loading" && (
          <div className="h-16 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          </div>
        )}

        {status === "approved" && (
          <div className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-6 text-center space-y-3">
              <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                <CheckCircle size={30} className="text-emerald-600" />
              </div>
              <div>
                <p className="font-bold text-slate-800 text-lg">You're an approved driver!</p>
                <p className="text-sm text-slate-500 mt-1">Open the mobile app to go online and start accepting deliveries.</p>
              </div>
            </div>

            {/* Bank details */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Lock size={15} className="text-slate-400" />
                  <p className="font-semibold text-slate-800 text-sm">Bank details for payouts</p>
                  <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">Encrypted</span>
                </div>
                <button
                  onClick={() => {
                    setUpdBankName(currentBankName ?? "");
                    setUpdBankSort(currentBankSort ?? "");
                    setUpdBankNumber("");
                    setShowBankForm(p => !p);
                  }}
                  className="flex items-center gap-1.5 text-xs font-semibold text-primary border border-primary/30 rounded-lg px-3 py-1.5 hover:bg-primary/5 transition-colors"
                >
                  <Edit2 size={12} />
                  {showBankForm ? "Cancel" : "Update"}
                </button>
              </div>

              {currentBankNumber ? (
                <div className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3">
                  <Banknote size={16} className="text-slate-400 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-slate-700">{currentBankName ?? "Account holder"}</p>
                    <p className="text-xs text-slate-500 font-mono mt-0.5">
                      Sort: {currentBankSort} &nbsp;·&nbsp; Acct: ****{currentBankNumber.slice(-4)}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-500">No bank details saved yet.</p>
              )}

              {showBankForm && (
                <div className="space-y-3 pt-1 border-t border-slate-100">
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Account holder name</label>
                    <input
                      type="text"
                      value={updBankName}
                      onChange={e => setUpdBankName(e.target.value)}
                      placeholder="Name as it appears on your bank account"
                      className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Sort code</label>
                      <input
                        type="text"
                        value={updBankSort}
                        onChange={e => setUpdBankSort(e.target.value)}
                        placeholder="00-00-00"
                        maxLength={8}
                        className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary font-mono"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">New account number</label>
                      <input
                        type="password"
                        value={updBankNumber}
                        onChange={e => setUpdBankNumber(e.target.value)}
                        placeholder="12345678"
                        maxLength={8}
                        className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary font-mono"
                      />
                    </div>
                  </div>
                  <button
                    onClick={() => void handleUpdateBank()}
                    disabled={savingBank}
                    className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold py-3 rounded-xl transition-opacity disabled:opacity-60 text-sm"
                  >
                    {savingBank
                      ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      : "Save bank details"
                    }
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {status === "pending" && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-100 rounded-2xl p-6 text-center space-y-3">
            <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto">
              <Clock size={28} className="text-amber-600" />
            </div>
            <div>
              <p className="font-bold text-slate-800 text-lg">Application received!</p>
              <p className="text-sm text-slate-500 mt-1">We're reviewing your application and will notify you as soon as it's approved.</p>
            </div>
          </div>
        )}

        {status === "rejected" && (
          <div className="bg-red-50 border border-red-100 rounded-2xl p-6 text-center space-y-3">
            <div>
              <p className="font-bold text-slate-800 text-lg">Application not approved</p>
              <p className="text-sm text-slate-500 mt-1">Your application wasn't approved at this time. Contact support if you have questions.</p>
            </div>
          </div>
        )}

        {/* Not logged in — CTA */}
        {status === "none" && !user && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 text-center space-y-4">
            <p className="text-slate-600 text-sm leading-relaxed">
              Create a free Ayrshire Hub account to apply as a driver.
            </p>
            <Link href="/signup">
              <button className="w-full bg-primary hover:bg-primary/90 text-white font-bold py-4 rounded-2xl transition-colors">
                Create Account &amp; Apply
              </button>
            </Link>
            <Link href="/login">
              <button className="w-full border border-slate-200 text-slate-600 py-3 rounded-2xl text-sm hover:bg-slate-50 transition-colors">
                Already have an account? Sign in
              </button>
            </Link>
          </div>
        )}

        {/* Application form */}
        {status === "none" && user && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-5">
            <h2 className="font-bold text-slate-800">Apply now</h2>

            {/* Vehicle type */}
            <div>
              <p className="text-sm font-semibold text-slate-600 mb-2">What vehicle will you use?</p>
              <div className="grid grid-cols-3 gap-2">
                {VEHICLES.map(({ value, label, icon: Icon, desc }) => {
                  const active = vehicleType === value;
                  return (
                    <button
                      key={value}
                      onClick={() => setVehicleType(value)}
                      className={`rounded-xl border p-3 text-left transition-colors ${active ? "border-primary bg-primary/5" : "border-slate-200 hover:border-slate-300"}`}
                    >
                      <Icon size={20} className={active ? "text-primary" : "text-slate-400"} />
                      <p className={`text-sm font-semibold mt-1.5 ${active ? "text-primary" : "text-slate-700"}`}>{label}</p>
                      <p className="text-xs text-slate-400 mt-0.5 leading-tight">{desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Licence */}
            <div>
              <label className="block text-sm font-semibold text-slate-600 mb-1.5">Driving licence number</label>
              <input
                type="text"
                value={licence}
                onChange={e => setLicence(e.target.value)}
                placeholder="e.g. SMITH123456AB1CD"
                className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary font-mono"
              />
            </div>

            {/* Vehicle Registration */}
            <div>
              <label className="block text-sm font-semibold text-slate-600 mb-1.5">Vehicle registration number</label>
              <input
                type="text"
                value={vehicleReg}
                onChange={e => setVehicleReg(e.target.value.toUpperCase())}
                placeholder="e.g. AB12 CDE"
                className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary font-mono"
              />
            </div>

            {/* Insurance declaration */}
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={isInsured}
                onChange={e => setIsInsured(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-primary flex-shrink-0"
              />
              <span className="text-sm text-slate-600 leading-relaxed">
                I confirm I hold valid vehicle insurance covering delivery driving, and my vehicle registration above is correct.
              </span>
            </label>

            {/* Bank details */}
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Lock size={14} className="text-slate-400" />
                <p className="text-sm font-semibold text-slate-700">Bank details for payouts</p>
                <span className="ml-auto text-xs text-slate-400 bg-white border border-slate-200 px-2 py-0.5 rounded-full">Encrypted</span>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed -mt-1">Stored securely and used only to transfer your earnings.</p>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Account holder name</label>
                <input
                  type="text"
                  value={bankAccountName}
                  onChange={e => setBankAccountName(e.target.value)}
                  placeholder="Name as it appears on your bank account"
                  className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Sort code</label>
                  <input
                    type="text"
                    value={bankSortCode}
                    onChange={e => setBankSortCode(e.target.value)}
                    placeholder="00-00-00"
                    maxLength={8}
                    className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary font-mono bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Account number</label>
                  <input
                    type="text"
                    value={bankAccountNumber}
                    onChange={e => setBankAccountNumber(e.target.value)}
                    placeholder="12345678"
                    maxLength={8}
                    className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary font-mono bg-white"
                  />
                </div>
              </div>
            </div>

            {/* Bio */}
            <div>
              <label className="block text-sm font-semibold text-slate-600 mb-1.5">Tell us about yourself</label>
              <textarea
                value={bio}
                onChange={e => setBio(e.target.value)}
                placeholder="A short paragraph about your experience, local knowledge, and why you'd like to drive with Ayrshire Hub…"
                rows={4}
                className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
              />
            </div>

            {/* Location note */}
            <div className="flex items-start gap-2.5 bg-sky-50 border border-sky-100 rounded-xl px-3.5 py-3">
              <MapPin size={14} className="text-sky-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-sky-700 leading-relaxed">
                Jobs are matched to your live location — you'll automatically see orders from stores within 6 miles of where you are.
              </p>
            </div>

            <button
              onClick={() => void handleSubmit()}
              disabled={submitting}
              className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold py-4 rounded-2xl transition-opacity disabled:opacity-60"
            >
              {submitting ? (
                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <Truck size={17} />
                  Submit Application
                </>
              )}
            </button>
          </div>
        )}

        {/* Requirements */}
        <div className="bg-slate-100 rounded-2xl p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Requirements</p>
          <ul className="space-y-1">
            {[
              "Valid UK driving licence (or equivalent for cyclists)",
              "Aged 18 or over",
              "Knowledge of Ayrshire roads and towns",
              "Smartphone with the Ayrshire Hub mobile app",
              // Both are conditions of working, not nice-to-haves — they match
              // section 8 of the driver terms, so a applicant cannot read one
              // list here and find a different obligation after signing up.
              "Motor insurance covering carriage of goods for hire and reward",
              "An insulated heat bag for every food delivery",
            ].map(r => (
              <li key={r} className="flex items-start gap-2 text-xs text-slate-500">
                <CheckCircle size={13} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                {r}
              </li>
            ))}
          </ul>
        </div>

        <p className="text-xs text-slate-400 text-center pb-4">
          Deliveries are managed through the{" "}
          <Link href="/" className="text-primary hover:underline">Ayrshire Hub mobile app</Link>.
          Once approved, download the app and go online to start earning.
        </p>
      </div>
    </div>
  );
}
